// States/provinces by country ISO code
// Only countries with states relevant to the app are included here.
// For unsupported countries, the state field falls back to a text input.

export const statesByCountry = {
  IN: [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    // Union Territories
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ],
  AE: [
    'Abu Dhabi',
    'Ajman',
    'Dubai',
    'Fujairah',
    'Ras Al Khaimah',
    'Sharjah',
    'Umm Al Quwain',
  ],
  SA: [
    'Al Bahah',
    'Al Jawf',
    'Al Madinah',
    'Al Qassim',
    'Asir',
    'Eastern Province',
    'Ha\'il',
    'Jazan',
    'Makkah',
    'Najran',
    'Northern Borders',
    'Riyadh',
    'Tabuk',
  ],
  US: [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ],
  GB: [
    'England',
    'Northern Ireland',
    'Scotland',
    'Wales',
  ],
  AU: [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
  ],
  CA: [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon',
  ],
  BD: [
    'Barishal',
    'Chattogram',
    'Dhaka',
    'Khulna',
    'Mymensingh',
    'Rajshahi',
    'Rangpur',
    'Sylhet',
  ],
  PK: [
    'Azad Kashmir',
    'Balochistan',
    'Gilgit-Baltistan',
    'Islamabad',
    'Khyber Pakhtunkhwa',
    'Punjab',
    'Sindh',
  ],
  LK: [
    'Central',
    'Eastern',
    'North Central',
    'North Western',
    'Northern',
    'Sabaragamuwa',
    'Southern',
    'Uva',
    'Western',
  ],
  NP: [
    'Bagmati',
    'Gandaki',
    'Karnali',
    'Koshi',
    'Lumbini',
    'Madhesh',
    'Sudurpashchim',
  ],
  OM: [
    'Ad Dakhiliyah',
    'Ad Dhahirah',
    'Al Batinah North',
    'Al Batinah South',
    'Al Buraimi',
    'Al Sharqiyah North',
    'Al Sharqiyah South',
    'Al Wusta',
    'Dhofar',
    'Musandam',
    'Muscat',
  ],
  KW: [
    'Al Ahmadi',
    'Al Asimah',
    'Al Farwaniyah',
    'Al Jahra',
    'Hawalli',
    'Mubarak Al-Kabeer',
  ],
  QA: [
    'Ad Dawhah',
    'Al Daayen',
    'Al Khawr',
    'Al Rayyan',
    'Al Shamal',
    'Al Wakrah',
    'Ash Shahaniyah',
    'Umm Salal',
  ],
  BH: [
    'Capital',
    'Muharraq',
    'Northern',
    'Southern',
  ],
  MY: [
    'Johor',
    'Kedah',
    'Kelantan',
    'Kuala Lumpur',
    'Labuan',
    'Malacca',
    'Negeri Sembilan',
    'Pahang',
    'Penang',
    'Perak',
    'Perlis',
    'Putrajaya',
    'Sabah',
    'Sarawak',
    'Selangor',
    'Terengganu',
  ],
  SG: [
    'Singapore',
  ],
};

// Helper: get states for a country ISO code, or null if not available
export const getStatesForCountry = (countryIso) => {
  if (!countryIso) return null;
  return statesByCountry[countryIso.toUpperCase()] || null;
};

// Map country name to ISO code (for StoreFormScreen where we have country name, not ISO)
const countryNameToIso = {
  'India': 'IN',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  'United States': 'US',
  'United Kingdom': 'GB',
  'Australia': 'AU',
  'Canada': 'CA',
  'Bangladesh': 'BD',
  'Pakistan': 'PK',
  'Sri Lanka': 'LK',
  'Nepal': 'NP',
  'Oman': 'OM',
  'Kuwait': 'KW',
  'Qatar': 'QA',
  'Bahrain': 'BH',
  'Malaysia': 'MY',
  'Singapore': 'SG',
};

export const getStatesForCountryName = (countryName) => {
  if (!countryName) return null;
  // Try as ISO code first (e.g., "IN"), then as country name (e.g., "India")
  const upper = countryName.toUpperCase();
  if (statesByCountry[upper]) return statesByCountry[upper];
  const iso = countryNameToIso[countryName];
  return iso ? statesByCountry[iso] : null;
};
